import React from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { useBlockGroupContext } from "components/form/Blocks";
import { AttachmentType } from "components/ui/AttachmentPicker";

import { useSaveActionsContext } from "context/save";

import useSectionFiveImagesFifteenTextsApi from "hooks/api/private/blocks/useSectionFiveImagesFifteenTextsApi";
import useMobileOrDesktopFile from "hooks/useMobileOrDesktopFile";
import { ButtonDeleteInterface } from "hooks/useTitleFunctionsTopBlock";

import {
    SectionFiveImagesFifteenTextsBody,
    SectionFiveImagesFifteenTextsDetails,
} from "types/api/blocks/sectionFiveImagesFifteenTexts";
import { DesktopMobileForm, LanguageEnum } from "types/api/commons";
import { DesktopOrMobile } from "types/enums";

import { converterAttachmentToFileWithPayload, createAttachmentType } from "utils/utils";
import { getExtraErrorsDescription, notEmptyMedia } from "utils/validators";

export type SectionProductFooterForm = {
    id: string | undefined;
    img_one_id: string | undefined;
    img_one: DesktopMobileForm;
    img_two_id: string | undefined;
    img_two: DesktopMobileForm;
    img_three_id: string | undefined;
    img_three: DesktopMobileForm;
    text: {
        [x in LanguageEnum]: {
            text_1: string;
            text_2: string;
            text_3: string;
            text_4: string;
            text_5: string;
            text_6: string;
            text_7: string;
            text_8: string;
            text_9: string;
            text_10: string;
            text_11: string;
            text_12: string;
        };
    };
};

type SectionProductFooterType = {
    buttonDelete: ButtonDeleteInterface;
    setImageOne: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    setImageTwo: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    setImageThree: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    formHook: FormHookType<SectionProductFooterForm>;
};

function useSectionProductFooter(): SectionProductFooterType {
    const { blockGroupCompositionId } = useBlockGroupContext();

    const {
        saveSectionFiveImagesFifteenTexts,
        getOneSectionFiveImagesFifteenTexts,
        deleteSectionFiveImagesFifteenTexts,
    } = useSectionFiveImagesFifteenTextsApi();

    const schema = React.useMemo<ObjectPropertyType>(
        () => ({
            type: "object",
            properties: {
                id: {
                    type: "simple",
                    validators: [],
                },
                img_one: notEmptyMedia,
                img_two: notEmptyMedia,
                img_three: notEmptyMedia,
                text: {
                    type: "collection",
                    of: {
                        type: "object",
                        properties: {
                            text_1: {
                                type: "simple",
                                validators: [],
                            },
                            text_2: {
                                type: "simple",
                                validators: [],
                            },
                            text_3: {
                                type: "simple",
                                validators: [],
                            },
                            text_4: {
                                type: "simple",
                                validators: [],
                            },
                            text_5: {
                                type: "simple",
                                validators: [],
                            },
                            text_6: {
                                type: "simple",
                                validators: [],
                            },
                            text_7: {
                                type: "simple",
                                validators: [],
                            },
                            text_8: {
                                type: "simple",
                                validators: [],
                            },
                            text_9: {
                                type: "simple",
                                validators: [],
                            },
                            text_10: {
                                type: "simple",
                                validators: [],
                            },
                            text_11: {
                                type: "simple",
                                validators: [],
                            },
                            text_12: {
                                type: "simple",
                                validators: [],
                            },
                        },
                    },
                },
            },
        }),
        []
    );

    const initialValues: SectionProductFooterForm = React.useMemo(() => {
        return {
            id: undefined,
            img_one_id: undefined,
            img_one: {
                id: undefined,
                desktop: createAttachmentType({ id: null, label: "Desktop" }),
                mobile: createAttachmentType({ id: null, label: "Mobile" }),
            },
            img_two_id: undefined,
            img_two: {
                id: undefined,
                desktop: createAttachmentType({ id: null, label: "Desktop" }),
                mobile: createAttachmentType({ id: null, label: "Mobile" }),
            },
            img_three_id: undefined,
            img_three: {
                id: undefined,
                desktop: createAttachmentType({ id: null, label: "Desktop" }),
                mobile: createAttachmentType({ id: null, label: "Mobile" }),
            },
            text: {
                it: {
                    text_1: "",
                    text_2: "",
                    text_3: "",
                    text_4: "",
                    text_5: "",
                    text_6: "",
                    text_7: "",
                    text_8: "",
                    text_9: "",
                    text_10: "",
                    text_11: "",
                    text_12: "",
                },
                en: {
                    text_1: "",
                    text_2: "",
                    text_3: "",
                    text_4: "",
                    text_5: "",
                    text_6: "",
                    text_7: "",
                    text_8: "",
                    text_9: "",
                    text_10: "",
                    text_11: "",
                    text_12: "",
                },
            },
        };
    }, []);

    const formHook = useForm<SectionProductFooterForm>(
        schema,
        initialValues,
        getExtraErrorsDescription
    );

    const { showAllError, setForm, isValid, form } = formHook;

    const [sectionProductFooterDetails, setSectionFiveImagesFifteenTextsDetails] = React.useState<
        SectionFiveImagesFifteenTextsDetails | undefined
    >(undefined);

    React.useEffect(() => {
        const [apiCall, abortController] = getOneSectionFiveImagesFifteenTexts(
            { id: blockGroupCompositionId },
            { rejectNotFound: true }
        );

        apiCall.then(setSectionFiveImagesFifteenTextsDetails).catch((error) => console.log(error));

        return () => abortController.abort();
    }, [
        setSectionFiveImagesFifteenTextsDetails,
        getOneSectionFiveImagesFifteenTexts,
        blockGroupCompositionId,
    ]);

    React.useEffect(() => {
        if (sectionProductFooterDetails) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.id = sectionProductFooterDetails.id;

                newForm.img_one_id = sectionProductFooterDetails.img_one_id;
                newForm.img_one = {
                    id: sectionProductFooterDetails.img_one?.id,
                    desktop: createAttachmentType({
                        id: sectionProductFooterDetails.img_one?.desktop.id,
                        label: "Desktop",
                        filename: sectionProductFooterDetails.img_one?.desktop.filename,
                        status: 2,
                    }),
                    mobile: createAttachmentType({
                        id: sectionProductFooterDetails.img_one?.mobile.id,
                        label: "Mobile",
                        filename: sectionProductFooterDetails.img_one?.mobile.filename,
                        status: 2,
                    }),
                };

                newForm.img_two_id = sectionProductFooterDetails.img_two_id;
                newForm.img_two = {
                    id: sectionProductFooterDetails.img_two?.id,
                    desktop: createAttachmentType({
                        id: sectionProductFooterDetails.img_two?.desktop.id,
                        label: "Desktop",
                        filename: sectionProductFooterDetails.img_two?.desktop.filename,
                        status: 2,
                    }),
                    mobile: createAttachmentType({
                        id: sectionProductFooterDetails.img_two?.mobile.id,
                        label: "Mobile",
                        filename: sectionProductFooterDetails.img_two?.mobile.filename,
                        status: 2,
                    }),
                };

                newForm.img_three_id = sectionProductFooterDetails.img_three_id;
                newForm.img_three = {
                    id: sectionProductFooterDetails.img_three?.id,
                    desktop: createAttachmentType({
                        id: sectionProductFooterDetails.img_three?.desktop.id,
                        label: "Desktop",
                        filename: sectionProductFooterDetails.img_three?.desktop.filename,
                        status: 2,
                    }),
                    mobile: createAttachmentType({
                        id: sectionProductFooterDetails.img_three?.mobile.id,
                        label: "Mobile",
                        filename: sectionProductFooterDetails.img_three?.mobile.filename,
                        status: 2,
                    }),
                };

                newForm.text.en.text_1 = sectionProductFooterDetails.text.en.text_1;
                newForm.text.en.text_2 = sectionProductFooterDetails.text.en.text_2;
                newForm.text.en.text_3 = sectionProductFooterDetails.text.en.text_3;
                newForm.text.en.text_4 = sectionProductFooterDetails.text.en.text_4;
                newForm.text.en.text_5 = sectionProductFooterDetails.text.en.text_5;
                newForm.text.en.text_6 = sectionProductFooterDetails.text.en.text_6;
                newForm.text.en.text_7 = sectionProductFooterDetails.text.en.text_7;
                newForm.text.en.text_8 = sectionProductFooterDetails.text.en.text_8;
                newForm.text.en.text_9 = sectionProductFooterDetails.text.en.text_9;
                newForm.text.en.text_10 = sectionProductFooterDetails.text.en.text_10;
                newForm.text.en.text_11 = sectionProductFooterDetails.text.en.text_11;
                newForm.text.en.text_12 = sectionProductFooterDetails.text.en.text_12;

                newForm.text.it.text_1 = sectionProductFooterDetails.text.it.text_1;
                newForm.text.it.text_2 = sectionProductFooterDetails.text.it.text_2;
                newForm.text.it.text_3 = sectionProductFooterDetails.text.it.text_3;
                newForm.text.it.text_4 = sectionProductFooterDetails.text.it.text_4;
                newForm.text.it.text_5 = sectionProductFooterDetails.text.it.text_5;
                newForm.text.it.text_6 = sectionProductFooterDetails.text.it.text_6;
                newForm.text.it.text_7 = sectionProductFooterDetails.text.it.text_7;
                newForm.text.it.text_8 = sectionProductFooterDetails.text.it.text_8;
                newForm.text.it.text_9 = sectionProductFooterDetails.text.it.text_9;
                newForm.text.it.text_10 = sectionProductFooterDetails.text.it.text_10;
                newForm.text.it.text_11 = sectionProductFooterDetails.text.it.text_11;
                newForm.text.it.text_12 = sectionProductFooterDetails.text.it.text_12;

                return newForm;
            });
        }
    }, [setForm, sectionProductFooterDetails]);

    const { addSaveAction } = useSaveActionsContext();

    React.useEffect(() => {
        addSaveAction(blockGroupCompositionId, () => {
            if (isValid) {
                const section3Images8TextsBody: SectionFiveImagesFifteenTextsBody = {
                    id: blockGroupCompositionId,
                    img_one: {
                        id: form.img_one.id,
                        desktop: converterAttachmentToFileWithPayload(form.img_one.desktop),
                        mobile: converterAttachmentToFileWithPayload(form.img_one.mobile),
                    },
                    img_two: {
                        id: form.img_two.id,
                        desktop: converterAttachmentToFileWithPayload(form.img_two.desktop),
                        mobile: converterAttachmentToFileWithPayload(form.img_two.mobile),
                    },
                    img_three: {
                        id: form.img_three.id,
                        desktop: converterAttachmentToFileWithPayload(form.img_three.desktop),
                        mobile: converterAttachmentToFileWithPayload(form.img_three.mobile),
                    },
                    img_four: {
                        id: undefined,
                        desktop: undefined,
                        mobile: undefined,
                    },
                    img_five: {
                        id: undefined,
                        desktop: undefined,
                        mobile: undefined,
                    },
                    text: {
                        it: {
                            text_1: form.text.it.text_1,
                            text_2: form.text.it.text_2,
                            text_3: form.text.it.text_3,
                            text_4: form.text.it.text_4,
                            text_5: form.text.it.text_5,
                            text_6: form.text.it.text_6,
                            text_7: form.text.it.text_7,
                            text_8: form.text.it.text_8,
                            text_9: form.text.it.text_9,
                            text_10: form.text.it.text_10,
                            text_11: form.text.it.text_11,
                            text_12: form.text.it.text_12,
                            text_13: "",
                            text_14: "",
                            text_15: "",
                        },
                        en: {
                            text_1: form.text.en.text_1,
                            text_2: form.text.en.text_2,
                            text_3: form.text.en.text_3,
                            text_4: form.text.en.text_4,
                            text_5: form.text.en.text_5,
                            text_6: form.text.en.text_6,
                            text_7: form.text.en.text_7,
                            text_8: form.text.en.text_8,
                            text_9: form.text.en.text_9,
                            text_10: form.text.en.text_10,
                            text_11: form.text.en.text_11,
                            text_12: form.text.en.text_12,
                            text_13: "",
                            text_14: "",
                            text_15: "",
                        },
                    },
                };

                const [apiCall] = saveSectionFiveImagesFifteenTexts(section3Images8TextsBody);

                const promises: Promise<unknown>[] = [];

                promises.push(apiCall);

                return Promise.all(promises);
            } else {
                showAllError();
                return Promise.reject("INVALID_FORM");
            }
        });
    }, [
        addSaveAction,
        blockGroupCompositionId,
        form,
        isValid,
        saveSectionFiveImagesFifteenTexts,
        showAllError,
    ]);

    const { setDesktopMobileImage: setImageOne } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "img_one",
    });

    const { setDesktopMobileImage: setImageTwo } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "img_two",
    });

    const { setDesktopMobileImage: setImageThree } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "img_three",
    });

    return React.useMemo(
        (): SectionProductFooterType => ({
            buttonDelete: deleteSectionFiveImagesFifteenTexts,
            setImageOne,
            setImageTwo,
            setImageThree,
            formHook,
        }),
        [deleteSectionFiveImagesFifteenTexts, setImageOne, setImageTwo, setImageThree, formHook]
    );
}

export default useSectionProductFooter;
